import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { DELETE_LEGAL_SETUP_CONFIG, GET_LEGAL_SETUP_CONFIGS, SET_LEGAL_SETUP_CONFIG } from './graphql';
import StoreAgeValidatorCard from './StoreAgeValidatorCard';
import UsOptOutCard from './UsOptOutCard';

type LegalSetupEntry = {
  id: string;
  adultAge: number;
  cmpAllowedAge: number;
  attAllowedAge: number;
  updatedAt?: string | null;
};

type LegalSetupRegion = {
  id: string;
  countries: string[];
};

type AgeDraft = {
  adultAge: string;
  cmpAllowedAge: string;
  attAllowedAge: string;
};

type Ages = {
  adultAge: number;
  cmpAllowedAge: number;
  attAllowedAge: number;
};

type CountryRow = {
  code: string;
  name: string;
  group: string;
  ages: Ages;
  source: string;
  hasOverride: boolean;
};

const HARDCODED_FALLBACK_AGE = 18;
const COUNTRY_CODE_REGEX = /^[A-Z]{2}$/;
const DEFAULT_SCOPE = 'DEFAULT';

const GROUP_LABELS: Record<string, string> = {
  [DEFAULT_SCOPE]: 'Rest of World',
  GDPR: 'GDPR (EU/EEA)',
  CA_US_COPPA: 'CA / US COPPA',
};

const GROUP_DESCRIPTIONS: Record<string, string> = {
  [DEFAULT_SCOPE]: 'Baseline for every country without a region or country row.',
  GDPR: 'All GDPR countries and territories (see the filter to list them).',
  CA_US_COPPA: 'Canada, USA and US territories. The COPPA under-13 hard block is separate and not editable here.',
};

const GROUP_CHIP_VARIANT: Record<string, string> = {
  [DEFAULT_SCOPE]: 'secondary',
  GDPR: 'success',
  CA_US_COPPA: 'primary',
};

const AGE_FIELDS: { key: keyof AgeDraft; label: string; help: string }[] = [
  { key: 'adultAge', label: 'Adult age', help: 'Below this age = kids mode' },
  { key: 'cmpAllowedAge', label: 'CMP age', help: 'Below this age = no CMP consent' },
  { key: 'attAllowedAge', label: 'ATT age', help: 'Below this age = no ATT prompt' },
];

const HARDCODED_AGES: Ages = {
  adultAge: HARDCODED_FALLBACK_AGE,
  cmpAllowedAge: HARDCODED_FALLBACK_AGE,
  attAllowedAge: HARDCODED_FALLBACK_AGE,
};

// Countries with special legal regimes, always shown even without a group or override.
// requiredAge is the reference value from the Legal SDK legislation config (v26.0.1).
const NOTABLE_COUNTRIES: Record<string, { note: string; requiredAge: number }> = {
  BR: { note: 'Block mode: underage players are blocked from the game. One-time age-gate re-prompt (2026-03-13).', requiredAge: 18 },
  RU: { note: 'No longer block mode: underage players play in kid mode like the rest of the world.', requiredAge: 18 },
  CN: { note: 'Legal SDK reference: 14. Not set in our game yet - use Edit to apply it (changes live behavior).', requiredAge: 14 },
};

type RegionDisplayNames = { of: (code: string) => string | undefined };
type IntlWithDisplayNames = {
  DisplayNames?: new (locales: string[], options: { type: string }) => RegionDisplayNames;
};

const countryDisplayNames = (() => {
  try {
    const DisplayNamesCtor = (Intl as unknown as IntlWithDisplayNames).DisplayNames;
    return DisplayNamesCtor ? new DisplayNamesCtor(['en'], { type: 'region' }) : null;
  } catch {
    return null;
  }
})();

const countryName = (code: string): string => {
  try {
    return countryDisplayNames?.of(code) ?? code;
  } catch {
    return code;
  }
};

const toDraft = (ages: Ages): AgeDraft => ({
  adultAge: String(ages.adultAge),
  cmpAllowedAge: String(ages.cmpAllowedAge),
  attAllowedAge: String(ages.attAllowedAge),
});

const parseDraft = (draft: AgeDraft): Ages | null => {
  const values = [draft.adultAge, draft.cmpAllowedAge, draft.attAllowedAge].map(value => Number(value));
  if (values.some(value => !Number.isInteger(value) || value < 0 || value > 99)) {
    return null;
  }
  return { adultAge: values[0], cmpAllowedAge: values[1], attAllowedAge: values[2] };
};

const agesOf = (entry?: LegalSetupEntry): Ages | null =>
  entry
    ? { adultAge: entry.adultAge, cmpAllowedAge: entry.cmpAllowedAge, attAllowedAge: entry.attAllowedAge }
    : null;

export default function LegalSetup() {
  const { data, loading, error, refetch } = useQuery(GET_LEGAL_SETUP_CONFIGS, { fetchPolicy: 'network-only' });
  const [setConfig, { loading: saving }] = useMutation(SET_LEGAL_SETUP_CONFIG);
  const [deleteConfig, { loading: deleting }] = useMutation(DELETE_LEGAL_SETUP_CONFIG);

  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('ALL');
  const [editingScope, setEditingScope] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<AgeDraft>(toDraft(HARDCODED_AGES));
  const [newCountry, setNewCountry] = useState('');

  const entries: LegalSetupEntry[] = useMemo(() => data?.liveOps?.legalSetupConfigs ?? [], [data]);
  const regions: LegalSetupRegion[] = useMemo(() => data?.liveOps?.legalSetupRegions ?? [], [data]);
  const blockModeCountries: string[] = useMemo(() => data?.liveOps?.legalSetupBlockModeCountries ?? [], [data]);

  const entriesById = useMemo(() => {
    const map = new Map<string, LegalSetupEntry>();
    entries.forEach(entry => map.set(entry.id, entry));
    return map;
  }, [entries]);

  const groupByCountry = useMemo(() => {
    const map = new Map<string, string>();
    regions.forEach(region => {
      region.countries.forEach(code => map.set(code, region.id));
    });
    return map;
  }, [regions]);

  const resolveAges = (code: string): { ages: Ages; source: string } => {
    const own = agesOf(entriesById.get(code));
    if (own) {
      return { ages: own, source: `Country override` };
    }
    const group = groupByCountry.get(code);
    const regionAges = group ? agesOf(entriesById.get(group)) : null;
    if (group && regionAges) {
      return { ages: regionAges, source: GROUP_LABELS[group] ?? group };
    }
    const defaultAges = agesOf(entriesById.get(DEFAULT_SCOPE));
    if (defaultAges) {
      return { ages: defaultAges, source: GROUP_LABELS[DEFAULT_SCOPE] };
    }
    return { ages: HARDCODED_AGES, source: `Hardcoded (${HARDCODED_FALLBACK_AGE})` };
  };

  const countryRows: CountryRow[] = useMemo(() => {
    const codes = new Set<string>();
    regions.forEach(region => region.countries.forEach(code => codes.add(code)));
    entries.forEach(entry => {
      if (COUNTRY_CODE_REGEX.test(entry.id)) {
        codes.add(entry.id);
      }
    });
    Object.keys(NOTABLE_COUNTRIES).forEach(code => codes.add(code));
    blockModeCountries.forEach(code => codes.add(code));
    if (editingScope && COUNTRY_CODE_REGEX.test(editingScope)) {
      codes.add(editingScope);
    }
    return Array.from(codes)
      .map(code => {
        const { ages, source } = resolveAges(code);
        return {
          code,
          name: countryName(code),
          group: groupByCountry.get(code) ?? DEFAULT_SCOPE,
          ages,
          source,
          hasOverride: entriesById.has(code),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, entries, entriesById, groupByCountry, editingScope, blockModeCountries]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return countryRows.filter(row => {
      if (groupFilter === 'OVERRIDES' && !row.hasOverride) {
        return false;
      }
      if (groupFilter !== 'ALL' && groupFilter !== 'OVERRIDES' && row.group !== groupFilter) {
        return false;
      }
      if (term && !row.name.toLowerCase().includes(term) && !row.code.toLowerCase().includes(term)) {
        return false;
      }
      return true;
    });
  }, [countryRows, search, groupFilter]);

  const busy = saving || deleting;

  const startEdit = (scope: string, ages: Ages) => {
    setEditingScope(scope);
    setEditDraft(toDraft(ages));
  };

  const saveScope = async (scope: string) => {
    setFeedback(null);
    const ages = parseDraft(editDraft);
    if (!ages) {
      setFeedback({ type: 'danger', message: `${scope}: ages must be whole numbers between 0 and 99.` });
      return;
    }
    try {
      await setConfig({ variables: { input: { id: scope, ...ages } } });
      await refetch();
      setEditingScope(null);
      setFeedback({ type: 'success', message: `${scope} saved. Live pods pick it up within ~1 minute.` });
    } catch (mutationError) {
      setFeedback({ type: 'danger', message: `${scope}: ${(mutationError as Error).message}` });
    }
  };

  const removeScope = async (scope: string) => {
    setFeedback(null);
    try {
      await deleteConfig({ variables: { id: scope } });
      await refetch();
      setEditingScope(null);
      setFeedback({
        type: 'success',
        message: `${scope} removed - it falls back to the next matching scope (or the hardcoded ${HARDCODED_FALLBACK_AGE}).`,
      });
    } catch (mutationError) {
      setFeedback({ type: 'danger', message: `${scope}: ${(mutationError as Error).message}` });
    }
  };

  const addCountry = () => {
    const code = newCountry.trim().toUpperCase();
    if (!COUNTRY_CODE_REGEX.test(code)) {
      setFeedback({ type: 'danger', message: 'Country must be a 2-letter ISO code, e.g. CN or BR.' });
      return;
    }
    setNewCountry('');
    setGroupFilter('ALL');
    setSearch(code);
    startEdit(code, resolveAges(code).ages);
  };

  const renderAgeCells = (scope: string, ages: Ages) =>
    editingScope === scope ? (
      <>
        {AGE_FIELDS.map(field => (
          <td key={field.key} style={{ minWidth: 85 }}>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              max={99}
              value={editDraft[field.key]}
              title={field.help}
              onChange={event => setEditDraft(previous => ({ ...previous, [field.key]: event.target.value }))}
            />
          </td>
        ))}
      </>
    ) : (
      <>
        {AGE_FIELDS.map(field => (
          <td key={field.key} className="text-center">
            <b>{ages[field.key]}</b>
          </td>
        ))}
      </>
    );

  const renderActions = (scope: string, ages: Ages, hasRow: boolean, removeLabel: string) =>
    editingScope === scope ? (
      <>
        <Button size="sm" variant="success" disabled={busy} onClick={() => saveScope(scope)}>
          Save
        </Button>{' '}
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setEditingScope(null)}>
          Cancel
        </Button>
      </>
    ) : (
      <>
        <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => startEdit(scope, ages)}>
          Edit
        </Button>{' '}
        {hasRow && (
          <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => removeScope(scope)}>
            {removeLabel}
          </Button>
        )}
      </>
    );

  if (loading && !data) {
    return <Spinner animation="border" className="m-4" />;
  }

  const filterChips: { key: string; label: string }[] = [
    { key: 'ALL', label: 'All' },
    { key: DEFAULT_SCOPE, label: GROUP_LABELS[DEFAULT_SCOPE] },
    { key: 'GDPR', label: GROUP_LABELS.GDPR },
    { key: 'CA_US_COPPA', label: GROUP_LABELS.CA_US_COPPA },
    { key: 'OVERRIDES', label: 'Has override' },
  ];

  return (
    <div className="m-3">
      <h3>Legal SDK Setup - Age Requirements by Country</h3>
      <Alert variant="info">
        These are the <b>live values</b> served to game clients via <code>getLegalSetup</code>: <b>Adult age</b> (below
        = kids mode), <b>CMP age</b> and <b>ATT age</b>. Resolution per player country:{' '}
        <b>country override → legislation group → Rest of World → hardcoded {HARDCODED_FALLBACK_AGE}</b>. Changes go
        live within ~1 minute, no deploy. Block-mode countries (BR) and Didomi notices are separate mechanisms and
        not editable here.
      </Alert>
      {error && <Alert variant="danger">{error.message}</Alert>}
      {feedback && (
        <Alert variant={feedback.type} onClose={() => setFeedback(null)} dismissible>
          {feedback.message}
        </Alert>
      )}

      <StoreAgeValidatorCard />

      <UsOptOutCard />

      <Card className="mb-4">
        <Card.Header>Legislation groups</Card.Header>
        <Card.Body>
          <Table bordered hover size="sm">
            <thead>
              <tr>
                <th>Group</th>
                {AGE_FIELDS.map(field => (
                  <th key={field.key} className="text-center" title={field.help}>
                    {field.label}
                  </th>
                ))}
                <th>Status</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[DEFAULT_SCOPE, 'GDPR', 'CA_US_COPPA'].map(scope => {
                const entry = entriesById.get(scope);
                const ages =
                  agesOf(entry) ??
                  (scope === DEFAULT_SCOPE ? HARDCODED_AGES : resolveAges(scope === 'GDPR' ? 'FR' : 'US').ages);
                return (
                  <tr key={scope}>
                    <td>
                      <Badge variant={GROUP_CHIP_VARIANT[scope]}>{GROUP_LABELS[scope]}</Badge>
                      <div className="text-muted small">{GROUP_DESCRIPTIONS[scope]}</div>
                    </td>
                    {renderAgeCells(scope, ages)}
                    <td>
                      {entry ? (
                        <Badge variant="success">Configured</Badge>
                      ) : (
                        <Badge variant="secondary">Inherits ({HARDCODED_FALLBACK_AGE})</Badge>
                      )}
                    </td>
                    <td>{renderActions(scope, ages, !!entry, 'Reset')}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Countries ({countryRows.length} listed + Rest of World fallback)</Card.Header>
        <Card.Body>
          <Row className="mb-3 align-items-center">
            <Col xs="auto">
              <Form.Control
                placeholder="Search country or code..."
                value={search}
                style={{ width: 240 }}
                onChange={event => setSearch(event.target.value)}
              />
            </Col>
            <Col xs="auto">
              {filterChips.map(chip => (
                <Button
                  key={chip.key}
                  size="sm"
                  className="mr-1"
                  variant={groupFilter === chip.key ? 'primary' : 'outline-secondary'}
                  onClick={() => setGroupFilter(chip.key)}
                >
                  {chip.label}
                </Button>
              ))}
            </Col>
            <Col xs="auto" className="ml-auto text-muted small">
              {filteredRows.length} of {countryRows.length} rows
            </Col>
          </Row>

          <Table bordered hover size="sm">
            <thead>
              <tr>
                <th>Country / Territory</th>
                <th>Code</th>
                <th>Legislation</th>
                {AGE_FIELDS.map(field => (
                  <th key={field.key} className="text-center" title={field.help}>
                    {field.label}
                  </th>
                ))}
                <th>Values from</th>
                <th>Notes</th>
                <th style={{ width: 190 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(row => (
                <tr key={row.code}>
                  <td>
                    <b>{row.name}</b>
                  </td>
                  <td>
                    <code>{row.code}</code>
                  </td>
                  <td>
                    <Badge variant={GROUP_CHIP_VARIANT[row.group] ?? 'secondary'}>
                      {GROUP_LABELS[row.group] ?? row.group}
                    </Badge>
                    {blockModeCountries.includes(row.code) && (
                      <>
                        {' '}
                        <Badge variant="danger" title="Underage players are blocked from the game instead of kids mode">
                          Block mode
                        </Badge>
                      </>
                    )}
                  </td>
                  {renderAgeCells(row.code, row.ages)}
                  <td>
                    {row.hasOverride ? <Badge variant="warning">Country override</Badge> : row.source}
                  </td>
                  <td className="small text-muted" style={{ maxWidth: 260 }}>
                    {NOTABLE_COUNTRIES[row.code]?.note ?? ''}
                    {NOTABLE_COUNTRIES[row.code] &&
                      NOTABLE_COUNTRIES[row.code].requiredAge !== row.ages.adultAge && (
                        <>
                          {' '}
                          <Badge variant="warning">Reference: {NOTABLE_COUNTRIES[row.code].requiredAge}</Badge>
                        </>
                      )}
                  </td>
                  <td>{renderActions(row.code, row.ages, row.hasOverride, 'Remove override')}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No countries match that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <hr />
          <Row className="align-items-center">
            <Col xs="auto">
              <b>Country not in the list?</b>{' '}
              <span className="text-muted small">
                Countries outside GDPR / CA-US-COPPA use Rest of World - add an override to give one its own numbers
                (e.g. CN).
              </span>
            </Col>
            <Col xs="auto">
              <Form.Control
                style={{ width: 90, display: 'inline-block' }}
                placeholder="CN"
                maxLength={2}
                value={newCountry}
                onChange={event => setNewCountry(event.target.value.toUpperCase())}
              />{' '}
              <Button disabled={busy || !newCountry.trim()} onClick={addCountry}>
                Add override
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}
